import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import {
  FormVariant,
  formVariantsOptions,
} from 'app/pages/loanApplication/tabs/tasks/Tasks.constants';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import {
  FormAutocompleteOption,
  onAutocompleteChangeInForm,
} from 'app/shared/components/form/Autocomplete';
import TextFieldBuilder from 'app/shared/components/form/TextFieldBuilder';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import clsx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';
import { entityAssignmentTypes } from 'constants/options';
import { DataType, EntityAssignmentType } from 'constants/taskCategories';
import { TaskAssignmentType } from 'constants/tasks';
import useFormsInfoLoader from 'hooks/useFormsInfoLoader';
import {
  checkIsBorrowerPairValidated,
  getBorrowerPairError,
  getCompatibleBorrowerOptions,
  getIncomeOwnerOption,
  getOptionsWithSelected,
  getSoleLegalAssignee,
  getTaskEntityOptions,
} from 'utils/taskEntity';

import {
  selectFormsInfoListOptions,
  selectLoanSpecificFormsListOptions,
  selectStateOptionsWithExistingFormsInfo,
} from 'store/forms/selectors';
import {
  selectAssetsWithTypeAndAmount,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
  selectIncomesWithTypeAndAmount,
  selectLoanApplicationBorrowerOptions,
} from 'store/loanApplications/selectors';
import { selectSlaGroupsOptions } from 'store/underwriting/selectors';

import { UnderwritingTestIds } from '../../Underwriting.testIds';

const AddConditionFormTaskType = ({
  taskType,
  conditionId,
  validationErrors,
  handleChange,
  setInForm,
  validateField,
  taskTypeDynamicFieldOptions,
  onCreateNewForm,
  isBrokerType = false,
  taskIndex = 0,
  formName,
  isInfoButtonHidden = false,
}) => {
  const isOldTaskAssignmentEngine = useFeatureToggle(FeatureFlag.OldTaskAssignmentEngine);
  const isNewTaskAssignmentEngine = !isOldTaskAssignmentEngine;
  const isSlaGroupsAssigneeSelected = taskType?.taskAssignmentType === TaskAssignmentType.SLA_GROUP;
  const isBorrowerEntityAssignmentType =
    taskType?.entityAssignmentType === EntityAssignmentType.BORROWER;
  const isIncomeEntityAssignmentType =
    taskType?.entityAssignmentType === EntityAssignmentType.INCOME;
  const shouldDeriveAssigneeFromIncome =
    isIncomeEntityAssignmentType && !isSlaGroupsAssigneeSelected;
  const isFormTaskType = taskType?.dataType === DataType.FORMS;

  useFormsInfoLoader(isFormTaskType);

  const borrowers = useSelector(selectLoanApplicationBorrowerOptions);
  const slaGroups = useSelector(selectSlaGroupsOptions);
  const assets = useSelector(selectAssetsWithTypeAndAmount);
  const incomes = useSelector(selectIncomesWithTypeAndAmount);
  const statesOptionsWithExistingForms = useSelector(selectStateOptionsWithExistingFormsInfo);
  const formsOptions = useSelector(selectFormsInfoListOptions);
  const loanSpecificFormsOptions = useSelector(selectLoanSpecificFormsListOptions);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(formName));
  const isPageLocked = useSelector(selectFormLockedPage(formName));

  const baseTaskTypePath = isBrokerType
    ? `conditionTaskTypes.${taskType.taskTypeId}`
    : `conditionTaskTypes.${conditionId}.conditionTaskTypes.${taskType.taskTypeId}`;

  const compatibleBorrowerOptions = useMemo(
    () =>
      isBorrowerEntityAssignmentType
        ? getCompatibleBorrowerOptions(borrowers, taskType.entity)
        : borrowers,
    [isBorrowerEntityAssignmentType, borrowers, taskType.entity],
  );

  const assigneeOptions = useMemo(
    () =>
      isSlaGroupsAssigneeSelected
        ? slaGroups.filter(({ value }) => value === taskType.slaGroupId)
        : getOptionsWithSelected(compatibleBorrowerOptions, taskType.assignee),
    [
      compatibleBorrowerOptions,
      isSlaGroupsAssigneeSelected,
      slaGroups,
      taskType.slaGroupId,
      taskType.assignee,
    ],
  );

  const isBorrowerPairValidated = checkIsBorrowerPairValidated(taskType);

  const borrowerPairError = useMemo(
    () =>
      isBorrowerPairValidated ? getBorrowerPairError(taskType.entity, taskType.assignee) : null,
    [isBorrowerPairValidated, taskType.entity, taskType.assignee],
  );

  // Locked only when the assignee is genuinely the single legal choice, so an invalid pair always
  // stays fixable from either side.
  const isSoleCompatibleAssignee =
    isBorrowerEntityAssignmentType &&
    !!getSoleLegalAssignee(borrowers, taskType.entity, taskType.assignee);

  // validateField is a dep so this closes over the same debounce instance onAssigneeChange flushes;
  // over a stale one the flush would drain a different queue and drop the assignee's validation.
  const onAutocompleteChange = useMemo(
    () => onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  // The submit-time pair check reports onto the ENTITY path, and validating the assignee alone
  // never re-runs it — so fixing the pair from this side would leave the stale error on screen
  // until the next Save. Re-validating the entity clears it; a still-invalid pair keeps rendering
  // the live borrowerPairError. Only where the schema actually carries that test, since validateAt
  // on an undeclared path stores yup's own "schema does not contain the path" as a field error.
  const onAssigneeChange = useCallback(
    (event, value) => {
      onAutocompleteChange(event, value);

      // Only an entity that is actually set can carry a stale pair error — getBorrowerPairError is
      // null whenever either side is unset — and re-validating an empty one paints "Required" on a
      // field the user has not touched yet.
      if (!isBorrowerPairValidated || !taskType.entity?.value) {
        return;
      }

      // validateField is ONE debounced instance shared by every field and trailing-only, so
      // queueing the entity would discard the assignee validation onAutocompleteChange just
      // queued. Let that one land before this one is scheduled.
      validateField.flush?.();
      validateField(`${baseTaskTypePath}.entity`, taskType.entity);
    },
    [
      onAutocompleteChange,
      validateField,
      isBorrowerPairValidated,
      baseTaskTypePath,
      taskType.entity,
    ],
  );

  const entityAssignmentTypeName = useMemo(
    () => entityAssignmentTypes.find(({ value }) => taskType.entityAssignmentType === value)?.name,
    [taskType],
  );

  // Never filtered by the assignee — filtering both ways let the single-option effects below
  // re-select each other and latch the pair (FH-1986). An incompatible combination is therefore
  // reachable, and is reported by borrowerPairError instead of silently dropping a selection.
  const selectedEntityOptions = useMemo(
    () =>
      getTaskEntityOptions({
        entityAssignmentType: taskType?.entityAssignmentType,
        borrowers,
        incomes,
        assets,
      }),
    [taskType?.entityAssignmentType, borrowers, incomes, assets],
  );

  const incomeOwnerOption = useMemo(
    () =>
      shouldDeriveAssigneeFromIncome ? getIncomeOwnerOption(taskType.entity, borrowers) : null,
    [shouldDeriveAssigneeFromIncome, taskType.entity, borrowers],
  );

  useEffect(() => {
    if (selectedEntityOptions?.length !== 1) {
      return;
    }

    setInForm(`${baseTaskTypePath}.entity`, selectedEntityOptions[0]);
  }, [selectedEntityOptions, baseTaskTypePath, setInForm]);

  useEffect(() => {
    if (assigneeOptions?.length !== 1) {
      return;
    }

    // A deliberate borrower pick is never overwritten because the list it is measured against
    // keeps it (getOptionsWithSelected), so an incompatible one leaves two options and returns
    // above. An SLA-group assignee is configuration rather than a user's choice, so a stale one is
    // still corrected.
    if (taskType.assignee?.value === assigneeOptions[0].value) {
      return;
    }

    setInForm(`${baseTaskTypePath}.assignee`, assigneeOptions[0]);
  }, [assigneeOptions, taskType.assignee, baseTaskTypePath, setInForm]);

  useEffect(() => {
    if (!shouldDeriveAssigneeFromIncome) {
      return;
    }

    setInForm(`${baseTaskTypePath}.assignee`, incomeOwnerOption);
  }, [shouldDeriveAssigneeFromIncome, incomeOwnerOption, baseTaskTypePath, setInForm]);

  const formVariantWithoutNewFormOptions = useMemo(() => formVariantsOptions, []);

  const filteredFormOptions = useMemo(() => {
    const filteredFormsOptions = formsOptions.filter(
      ({ state }) => state === taskType?.state?.value,
    );

    if (filteredFormsOptions.length) {
      return filteredFormsOptions;
    }

    return formsOptions;
  }, [formsOptions, taskType?.state?.value]);

  const onGroupOptions = useCallback(({ group }) => group, []);
  const taskTypeValidationErrors = _.get(validationErrors, baseTaskTypePath);
  const isAllFormsSelected = taskType?.formVariant?.value === FormVariant.ALL_FORMS;

  useEffect(() => {
    if (isBrokerType) {
      return;
    }

    setInForm(`${baseTaskTypePath}.formId`, undefined);
  }, [baseTaskTypePath, setInForm, taskType.state, isBrokerType]);

  useEffect(() => {
    if (isBrokerType) {
      return;
    }

    setInForm(`${baseTaskTypePath}.state`, undefined);
  }, [baseTaskTypePath, setInForm, taskType.formVariant, isBrokerType]);

  useEffect(() => {
    if (taskType.formVariant?.value !== FormVariant.CREATE_NEW_FORM) {
      return;
    }

    onCreateNewForm({ conditionId, taskTypeId: taskType.taskTypeId, taskIndex });
  }, [conditionId, onCreateNewForm, taskIndex, taskType.formVariant, taskType.taskTypeId]);

  useEffect(() => {
    if (!_.isString(taskType.formId)) {
      return;
    }

    const formId = loanSpecificFormsOptions.find(({ value }) => value === taskType.formId);

    setInForm(`${baseTaskTypePath}.formId`, formId);
  }, [baseTaskTypePath, loanSpecificFormsOptions, onCreateNewForm, setInForm, taskType.formId]);

  return (
    <div
      className={clsx('gap-8 items-center flex-wrap relative', { flex: !isBrokerType })}
      key={taskType.taskTypeId}
    >
      {isNewTaskAssignmentEngine && (
        <AutocompleteCDS
          disableClearable
          required
          className="flex-1"
          containerClassName="mb-24"
          dataTestIdInput={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_ASSIGNEE_SELECT}
          dataTestIdOption={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_ASSIGNEE_OPTION}
          // Income tasks derive the borrower from the income's owner server-side
          // (Skynet.Loans FH-1730), so the assignee is auto-set and locked, not picked.
          disabled={
            isSlaGroupsAssigneeSelected ||
            shouldDeriveAssigneeFromIncome ||
            isPageLocked ||
            lockedFieldsMap.assignee ||
            isSoleCompatibleAssignee
          }
          error={!!taskTypeValidationErrors?.assignee}
          helperText={taskTypeValidationErrors?.assignee}
          label="Assignee"
          name={`${baseTaskTypePath}.assignee`}
          options={assigneeOptions}
          value={taskType.assignee}
          onChange={onAssigneeChange}
        />
      )}

      {selectedEntityOptions && isNewTaskAssignmentEngine && (
        <AutocompleteCDS
          disableClearable
          required
          className="flex-1"
          containerClassName="mb-24"
          dataTestIdInput={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_ENTITY_SELECT}
          dataTestIdOption={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_ENTITY_OPTION}
          disabled={isPageLocked || lockedFieldsMap.entity}
          error={!!taskTypeValidationErrors?.entity || !!borrowerPairError}
          helperText={taskTypeValidationErrors?.entity || borrowerPairError}
          label={entityAssignmentTypeName || 'Entity'}
          name={`${baseTaskTypePath}.entity`}
          optionName={isBorrowerEntityAssignmentType ? 'name' : 'typeAndAmount'}
          options={selectedEntityOptions}
          value={taskType.entity}
          onChange={onAutocompleteChange}
        />
      )}

      {isFormTaskType && (
        <>
          <AutocompleteCDS
            disableClearable
            required
            containerClassName="mb-24"
            dataTestIdInput={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_FORM_TYPE_SELECT}
            dataTestIdOption={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_FORM_TYPE_OPTION}
            disabled={isPageLocked || lockedFieldsMap.formVariant}
            error={!!taskTypeValidationErrors?.formVariant}
            helperText={taskTypeValidationErrors?.formVariant}
            id={`${baseTaskTypePath}.formVariant`}
            label="Form Type"
            name={`${baseTaskTypePath}.formVariant`}
            options={formVariantWithoutNewFormOptions}
            value={taskType.formVariant}
            onChange={onAutocompleteChange}
          />

          {isAllFormsSelected && (
            <AutocompleteCDS
              containerClassName="mb-24"
              dataTestId={UnderwritingTestIds.BROKER_CONDITION_FORM_STATE_SELECT}
              disabled={isPageLocked || lockedFieldsMap.state}
              error={!!taskTypeValidationErrors?.state}
              helperText={taskTypeValidationErrors?.state}
              label="State"
              name={`${baseTaskTypePath}.state`}
              options={statesOptionsWithExistingForms}
              value={taskType.state}
              onChange={onAutocompleteChange}
            />
          )}

          <AutocompleteCDS
            required
            OptionContent={FormAutocompleteOption}
            containerClassName="mb-24"
            dataTestId={UnderwritingTestIds.BROKER_CONDITION_FORM_FORM_ID_SELECT}
            disabled={isPageLocked || lockedFieldsMap.formId}
            error={!!taskTypeValidationErrors?.formId}
            helperText={taskTypeValidationErrors?.formId}
            label="Form"
            name={`${baseTaskTypePath}.formId`}
            optionKey="value"
            options={isAllFormsSelected ? filteredFormOptions : loanSpecificFormsOptions}
            value={taskType.formId}
            onChange={onAutocompleteChange}
          />
        </>
      )}

      {!isBrokerType && !isInfoButtonHidden && (
        <ButtonWithTooltip
          disablePortal
          className="mb-24"
          data-testid={UnderwritingTestIds.BROKER_CONDITION_FORM_TASK_TYPE_INFO_BUTTON}
          icon={InfoIcon}
          iconSize={IconSize[24]}
          placement="top"
          spanContainerClassName="ml-auto"
          title={<span>Task type: {taskType.name}</span>}
        />
      )}

      <TextFieldBuilder
        fullWidth
        required
        className="mb-24"
        containerClassName="description"
        data-testid={UnderwritingTestIds.ADD_CONDITION_FORM_DESCRIPTION_INPUT}
        disabled={isPageLocked || lockedFieldsMap.description}
        error={!!taskTypeValidationErrors?.description}
        groupBy={onGroupOptions}
        handleChange={handleChange}
        helperText={taskTypeValidationErrors?.description}
        id={`${baseTaskTypePath}.description`}
        label="Description"
        maxLength={4000}
        name={`${baseTaskTypePath}.description`}
        options={taskTypeDynamicFieldOptions}
        rows={3}
        setInForm={setInForm}
        validateField={validateField}
        value={taskType.description}
      />
    </div>
  );
};

AddConditionFormTaskType.propTypes = {
  taskType: PropTypes.object.isRequired,
  validationErrors: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  setInForm: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  taskTypeDynamicFieldOptions: PropTypes.array.isRequired,
  conditionId: PropTypes.string.isRequired,
  onCreateNewForm: PropTypes.func.isRequired,
  isBrokerType: PropTypes.bool,
  formName: PropTypes.string,
  taskIndex: PropTypes.number,
  isInfoButtonHidden: PropTypes.bool,
};

export default AddConditionFormTaskType;
